import { useRef, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Disc, Plus, Search, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type RecordLabelRow = {
  id: number
  name: string
  slug: string
  recordings_count: number
}

type Props = {
  record_labels: RecordLabelRow[]
  total_count: number
  search: string | null
  can_create: boolean
}

function statusText(labels: RecordLabelRow[], total_count: number, search: string | null): string {
  if (search) {
    const count = labels.length
    return `${count.toLocaleString()} label${count !== 1 ? "s" : ""} matching "${search}"`
  }
  return `${total_count.toLocaleString()} record label${total_count !== 1 ? "s" : ""} in the database`
}

export default function RecordLabelsIndex({ record_labels, total_count, search, can_create }: Props) {
  const [query, setQuery] = useState(search ?? "")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleQueryChange(value: string) {
    setQuery(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      router.get(
        "/record_labels",
        value.trim() ? { search: value.trim() } : {},
        { preserveState: true, preserveScroll: true }
      )
    }, 300)
  }

  function clearSearch() {
    setQuery("")
    router.get("/record_labels", {}, { preserveState: true, preserveScroll: true })
  }

  return (
    <>
      <Head title="Record Labels">
        <meta name="description" content="Browse all Australian music record labels in the database, with their releases and recording history." />
        <meta property="og:title" content="Record Labels" />
        <meta property="og:description" content="Browse all Australian music record labels in the database, with their releases and recording history." />
      </Head>
      <PublicShell>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="mb-1">Record Labels</h1>
              <p className="text-sm text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                {statusText(record_labels, total_count, search)}
              </p>
            </div>
            {can_create && (
              <Link
                href="/record_labels/new"
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-3 py-1.5 no-underline transition-colors shrink-0"
              >
                <Plus className="w-4 h-4" /> New label
              </Link>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="Search labels…"
              className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 pl-9 pr-9 py-2.5 text-sm text-stone-900 dark:text-white placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:focus:ring-amber-500 transition"
            />
            {query && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* List */}
          {record_labels.length === 0 ? (
            <p className="text-sm text-stone-500 dark:text-stone-400 py-8 text-center">
              {search ? `No labels found matching "${search}".` : "No record labels in the database yet."}
            </p>
          ) : (
            <ul className="divide-y divide-stone-100 dark:divide-stone-800 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 overflow-hidden">
              {record_labels.map((label) => (
                <li key={label.id}>
                  <Link
                    href={`/record_labels/${label.slug}`}
                    className="flex items-center gap-3 px-4 py-3.5 no-underline hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors group"
                  >
                    <Disc className="w-4 h-4 shrink-0 text-stone-300 dark:text-stone-600 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <span
                        className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors"
                        style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                      >
                        {label.name}
                      </span>
                    </div>
                    <span
                      className="text-xs text-stone-400 dark:text-stone-500 shrink-0"
                      style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                    >
                      {label.recordings_count} {label.recordings_count === 1 ? "recording" : "recordings"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PublicShell>
    </>
  )
}
